import "./setupMonaco";
import * as uuid from "uuid";
import * as React from "react";
import * as monaco from "monaco-editor";
import MonacoEditor, {
  DiffEditor as MonacoDiffEditor,
} from "@monaco-editor/react";
import style from "./style.module.css";
import { useEffect, useState } from "react";

interface EditorProps {
  language: string;
  value?: string;
  height?: string | number;
  width?: string | number;
  theme?: string;
  markers?: monaco.editor.IMarkerData[];
  readOnly?: boolean;
  onChange?: (newValue: string) => void;
  onEditorDidMount?: (
    editor: monaco.editor.IStandaloneCodeEditor,
    monacoModule: typeof monaco,
  ) => void;
  onEditorWillMount?: (monacoModule: typeof monaco) => void;
}

export function Editor(props: EditorProps) {
  const [monacoModule, setMonacoModule] = useState<typeof monaco | undefined>(
    undefined,
  );
  const [editor, setEditor] = useState<
    monaco.editor.IStandaloneCodeEditor | undefined
  >(undefined);
  const [owner] = useState(uuid.v4());

  useEffect(() => {
    if (!editor || !monacoModule) {
      return;
    }
    const model = editor.getModel();
    if (!model) {
      return;
    }
    const markers = (props.markers ?? []).map((marker) => {
      const ret = { ...marker };
      if (ret.startColumn <= 0) {
        ret.startColumn = 1;
      }
      if (ret.endColumn <= 0) {
        ret.endColumn = model.getLineLength(ret.startLineNumber) + 1;
      }
      return ret;
    });
    monacoModule.editor.setModelMarkers(model, owner, markers);
  }, [props.markers]);

  const onChange = (newValue: string | undefined) => {
    if (props.onChange != null) {
      props.onChange(newValue ?? "");
    }
  };

  const onEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    monacoModule: typeof monaco,
  ) => {
    setEditor(editor);
    setMonacoModule(monacoModule);
    if (props.onEditorDidMount != null) {
      props.onEditorDidMount(editor, monacoModule);
    }
  };

  const onEditorWillMount = (monacoModule: typeof monaco) => {
    if (props.onEditorWillMount != null) {
      props.onEditorWillMount(monacoModule);
    }
  };

  return (
    <div className={style.editor}>
      <MonacoEditor
        height={props.height || 400}
        width={props.width}
        language={props.language}
        theme={props.theme || "vs-light"}
        options={{
          selectOnLineNumbers: true,
          automaticLayout: true,
          fixedOverflowWidgets: true,
          folding: true,
          readOnly: props.readOnly ?? false,
          "semanticHighlighting.enabled": true,
          formatOnType: true,
          stickyScroll: {
            enabled: true,
          },
          tabSize: 2,
          insertSpaces: true,
        }}
        value={props.value || ""}
        onChange={onChange}
        onMount={onEditorDidMount}
        beforeMount={onEditorWillMount}
      />
    </div>
  );
}

interface DiffEditorProps {
  language: string;
  original?: string;
  modified?: string;
  height?: string | number;
  width?: string | number;
  theme?: string;
  markers?: monaco.editor.IMarkerData[];
  readOnly?: boolean;
  onChange?: (newValue: string) => void;
  onEditorDidMount?: (
    editor: monaco.editor.IStandaloneDiffEditor,
    monacoModule: typeof monaco,
  ) => void;
  onEditorWillMount?: (monacoModule: typeof monaco) => void;
}

export function DiffEditor(props: DiffEditorProps) {
  const [monacoModule, setMonacoModule] = useState<typeof monaco | undefined>(
    undefined,
  );
  const [editor, setEditor] = useState<
    monaco.editor.IStandaloneDiffEditor | undefined
  >(undefined);
  const [owner] = useState(uuid.v4());

  useEffect(() => {
    if (!editor) {
      return;
    }
    return () => editor.setModel(null);
  }, [editor]);

  useEffect(() => {
    if (!editor || !monacoModule) {
      return;
    }
    const model = editor.getModel();
    if (!model) {
      return;
    }
    monacoModule.editor.setModelMarkers(
      model.modified,
      owner,
      props.markers ?? [],
    );
  }, [props.markers]);

  const onChange = (newValue: string | undefined) => {
    if (props.onChange != null) {
      props.onChange(newValue ?? "");
    }
  };

  const onEditorDidMount = (
    editor: monaco.editor.IStandaloneDiffEditor,
    monacoModule: typeof monaco,
  ) => {
    setEditor(editor);
    setMonacoModule(monacoModule);
    if (props.onEditorDidMount != null) {
      props.onEditorDidMount(editor, monacoModule);
    }
    const model = editor.getModel()?.modified;
    if (model) {
      model.onDidChangeContent(() => {
        onChange(model.getValue());
      });
    }
  };

  const onEditorWillMount = (monacoModule: typeof monaco) => {
    if (props.onEditorWillMount != null) {
      props.onEditorWillMount(monacoModule);
    }
  };

  return (
    <div className={style.editor}>
      <MonacoDiffEditor
        height={props.height || 400}
        width={props.width}
        language={props.language}
        theme={props.theme || "vs-light"}
        options={{
          selectOnLineNumbers: true,
          automaticLayout: true,
          fixedOverflowWidgets: true,
          folding: true,
          readOnly: props.readOnly ?? false,
          renderSideBySide: false,
          formatOnType: true,
          stickyScroll: {
            enabled: true,
          },
        }}
        original={props.original || ""}
        modified={props.modified || ""}
        onMount={onEditorDidMount}
        beforeMount={onEditorWillMount}
      />
    </div>
  );
}
